import { formatMoney } from "./money";

/*
 * The affiliate programme, which this dashboard advertises and does not run.
 *
 * Two origins, and both are optional, which is the difference from
 * `VITE_API_URL`. A deployment with no programme is complete: the rail has no
 * referral row and the page does not exist. Failing the build over a missing
 * value would refuse to build a dashboard that is not missing anything.
 *
 * - `VITE_AFFILIATE_URL` is the affiliate dashboard, where somebody signs in
 *   with this same Maxlayer account and gets their link. Deciding whether the
 *   programme is offered at all hangs off this one.
 * - `VITE_AFFILIATE_API_URL` is the content API that holds the programme's
 *   terms. Without it the page still offers the programme, and says so without
 *   figures rather than with invented ones.
 */
function bareOrigin(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" || url.protocol === "http:" ? url.origin : null;
  } catch {
    return null;
  }
}

export const AFFILIATE_URL = bareOrigin(import.meta.env.VITE_AFFILIATE_URL as string | undefined);

export const AFFILIATE_API_URL = bareOrigin(
  import.meta.env.VITE_AFFILIATE_API_URL as string | undefined,
);

/** `GET /v1/affiliate/program`, as `serializeProgram` in the content API writes it. */
export interface AffiliateProgram {
  name: string;
  /** Basis points: 5000 is 50%. */
  commissionBps: number;
  commissionCapMicros: number;
  minPayoutMicros: number;
  cookieDays: number;
  holdDays: number;
  acceptingReferrals: boolean;
  termsUrl: string | null;
}

/** "50%", and "12.5%" rather than "12.50%" — the same rule the affiliate dashboard prints by. */
export function formatCommissionRate(bps: number): string {
  const percent = bps / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
}

/**
 * What one referred customer's first top-up earns, in micros.
 *
 * The content API's own arithmetic, `min(basis * rate, cap)` on integer micros,
 * so an estimate on this page cannot promise more than a commission row would
 * record.
 */
export function commissionFor(program: AffiliateProgram, topUpMicros: number): number {
  return Math.min(
    Math.floor((topUpMicros * program.commissionBps) / 10_000),
    program.commissionCapMicros,
  );
}

/**
 * An amount from the terms: "$100" rather than "$100.00".
 *
 * These are round figures an operator chose, and the cents on a cap or a
 * minimum are noise. An amount that is not whole keeps them.
 */
export function formatTermsAmount(micros: number): string {
  if (micros % 1_000_000 !== 0) return formatMoney(micros);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(micros / 1_000_000);
}

/**
 * The terms, read with no cookie.
 *
 * The content API answers this dashboard's origin with `*` and no credentials,
 * and a browser refuses a credentialed request against that — so `omit` is not
 * a nicety here, it is what makes the read work at all.
 */
export async function fetchAffiliateProgram(): Promise<AffiliateProgram> {
  if (!AFFILIATE_API_URL) throw new Error("No affiliate API is configured for this deployment.");

  const response = await fetch(`${AFFILIATE_API_URL}/v1/affiliate/program`, {
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`The affiliate API answered ${response.status}.`);

  const body = (await response.json()) as { program: AffiliateProgram };
  return body.program;
}
